<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    @update:modelValue="updateDrawerVisibility"
    direction="right"
    width="600px"
    title="Export Mailboxes"
  >
    <a-form>
      <div class="form_col mb-4">
        <span>Choose Mailbox Provider</span>
        <a-select v-model:value="selectedProvider" placeholder="Choose Mailbox Provider">
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_white_arrow"
              class="size-4"
            />
          </template>
          <a-select-option value="SMTP">SMTP</a-select-option>
        </a-select>
      </div>
    </a-form>
    <div v-if="mailboxes.length > 0" class="mailbox-list">
      <a-input
        v-model="searchQuery"
        placeholder="Search mailboxes"
        class="input_medium mb-4"
      />
      <a-checkbox
        :checked="allSelected"
        @change="toggleSelectAll"
        class="mb-2"
      >
        Select all
      </a-checkbox>
      <a-list
        :data-source="filteredMailboxes"
        bordered
        class="mailbox-list"
        style="max-height: 300px; overflow-y: auto;"
      >
        <template #renderItem="{ item }">
          <a-list-item>
            <a-checkbox
              :checked="selectedMailboxes.includes(item.id)"
              @change="() => toggleSelectMailbox(item.id)"
            >
              {{ item.email }}
            </a-checkbox>
          </a-list-item>
        </template>
      </a-list>
    </div>
    <template #footer>
      <div class="flex flex-row gap-2 justify-end">
        <a-button class="secondary_medium" @click="onCancel">Cancel</a-button>
        <a-button class="primary_medium" @click="onExport" :loading="loading">
          Export
        </a-button>
      </div>
    </template>
    <BulkExportModal
      source="mailboxes"
      :visible="showExportModal"
      title="Export Mailboxes"
      description="Selected mailboxes will be exported to your email"
      :selectCount="selectedMailboxes.length"
      @update:visible="val => showExportModal = val"
      @confirm="handleExportConfirm"
    />
  </CustomDrawer>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import BulkExportModal from "@/src/components/custom_table_components/BulkExportModal.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  drawerVisible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:drawerVisible"]);

const internalDrawerVisible = ref(props.drawerVisible);
const selectedProvider = ref(null);
const loading = ref(false);
const mailboxes = ref<any[]>([]);
const selectedMailboxes = ref<number[]>([]);
const searchQuery = ref("");
const showExportModal = ref(false);
const exportEmail = ref("");

const allSelected = computed(() =>
  filteredMailboxes.value.length > 0 &&
  filteredMailboxes.value.every(mb => selectedMailboxes.value.includes(mb.id))
);

const filteredMailboxes = computed(() => {
  if (!searchQuery.value) return mailboxes.value;
  return mailboxes.value.filter(mb =>
    mb.email.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const fetchMailboxes = async () => {
  loading.value = true;
  try {
    const response = await DashboardService.getMailboxesByVendor({vendor: selectedProvider.value});
    mailboxes.value = response.mailboxes || [];
    selectedMailboxes.value = [];
  } catch (e) {
    message.error("Failed to fetch mailboxes.");
  } finally {
    loading.value = false;
  }
};

watch(() => props.drawerVisible, (newValue) => {
  internalDrawerVisible.value = newValue;
});

watch(selectedProvider, (val) => {
  if (val === "SMTP") {
    fetchMailboxes();
  } else {
    mailboxes.value = [];
    selectedMailboxes.value = [];
  }
});

const updateDrawerVisibility = (value) => {
  emit("update:drawerVisible", value);
};

const onCancel = () => {
  updateDrawerVisibility(false);
};

const toggleSelectAll = (e) => {
  if (e.target.checked) {
    selectedMailboxes.value = filteredMailboxes.value.map(mb => mb.id);
  } else {
    selectedMailboxes.value = [];
  }
};

const toggleSelectMailbox = (id: number) => {
  if (selectedMailboxes.value.includes(id)) {
    selectedMailboxes.value = selectedMailboxes.value.filter(e => e !== id);
  } else {
    selectedMailboxes.value.push(id);
  }
};

const onExport = () => {
  if (!selectedProvider.value) {
    message.error("Please select a mailbox vendor.");
    return;
  }
  if (selectedMailboxes.value.length === 0) {
    message.error("Please select at least one mailbox to export.");
    return;
  }
  showExportModal.value = true;
};

const handleExportConfirm = async ({ email }) => {
  loading.value = true;
  try {
    const payload = {
      mailbox_ids: selectedMailboxes.value,
      vendor: selectedProvider.value,
      email // or export_email if that's what your backend expects
    };
    const response = await DashboardService.exportMailboxes(payload);
    if (response.success) {
      message.success("Export started successfully. You will be notified upon completion.");
      updateDrawerVisibility(false);
    } else {
      message.error(response.message || "Failed to start export.");
    }
  } catch (error) {
    message.error("An error occurred during export.");
  } finally {
    loading.value = false;
    showExportModal.value = false;
  }
};
</script> 